import { AppDispatch } from '@/store'
import { CartTypes } from './CartReducer'
import { BasicAuthServiceHelper } from '@/modules/Auth'
import APIRoutes from '@/modules/PetSitter/APIRoutes'

export interface CartProduct {
  offer: string
  start: string
  end: string
}

export interface CartData {
  products: CartProduct[]
  petsitters: string[]
  animals: string[]
  message: string
  allowAutoRebound: boolean
  allowTenders: boolean
  immediateTenders?: boolean
  rejectedPetsitters?: {
    '@context': string
    '@id': string
    '@type': string
    petsitter: string
    reason: string
  }[]
}

/* *******************************
  API OPERATIONS
******************************* */

export const getCartAsync = async (): Promise<CartData> => {
  const { path, method } = APIRoutes.GetCart
  return await BasicAuthServiceHelper.requestAuthenticatedAsync<CartData>(path, method)
}

export const updateCartAsync = async (data: CartData) => {
  const { path, method } = APIRoutes.UpdateCart
  return await BasicAuthServiceHelper.requestAuthenticatedAsync(path, method, data)
}

export const checkoutCartAsync = async () => {
  const { path, method } = APIRoutes.CheckoutCart
  return await BasicAuthServiceHelper.requestAuthenticatedAsync(path, method)
}

export const checkCartAsync = async (data: CartData) => {
  const { path, method } = APIRoutes.CheckCart
  return await BasicAuthServiceHelper.requestAuthenticatedAsync(path, method, data)
}

/* *******************************
  CART OPERATIONS
******************************* */

export const dispatchAddPetsitterToCart =
  (dispatch: AppDispatch, cart: CartData) =>
  async (petsitterId: string): Promise<void> => {
    try {
      const currentCart = cart
      if (currentCart.petsitters.includes(petsitterId)) return
      const updatedCart: CartData = {
        ...currentCart,
        petsitters: [...currentCart.petsitters, petsitterId],
      }

      await updateCartAsync(updatedCart)
      dispatch({ type: CartTypes.UpdateCart, payload: updatedCart })
    } catch (error) {
      console.error('Failed to add petsitter to cart:', error)
    }
  }

export const dispatchRemovePetsitterFromCart =
  (dispatch: AppDispatch, cart: CartData) =>
  async (petsitterId: string): Promise<void> => {
    try {
      const currentCart = cart
      if (!currentCart.petsitters.includes(petsitterId)) return

      const updatedCart: CartData = {
        ...currentCart,
        petsitters: currentCart.petsitters.filter((id) => id !== petsitterId),
      }

      await updateCartAsync(updatedCart)
      dispatch({ type: CartTypes.UpdateCart, payload: updatedCart })
    } catch (error) {
      console.error('Failed to remove petsitter from cart:', error)
    }
  }

export const dispatchLoadCartFromAPI = (dispatch: AppDispatch) => async (): Promise<void> => {
  try {
    const cartData = await getCartAsync()
    dispatch({ type: CartTypes.UpdateCart, payload: cartData })
  } catch (error) {
    console.error('Failed to load cart:', error)
  }
}

export const dispatchUpdateCart =
  (dispatch: AppDispatch, cart: CartData) =>
  async (updatedData: Partial<CartData>): Promise<void> => {
    try {
      const updatedCart: CartData = {
        ...cart,
        products: updatedData.products ?? cart.products,
        petsitters: updatedData.petsitters ?? cart.petsitters,
        animals: updatedData.animals ?? cart.animals,
        message: updatedData.message ?? cart.message,
        allowAutoRebound: updatedData.allowAutoRebound ?? cart.allowAutoRebound,
        allowTenders: updatedData.allowTenders ?? cart.allowTenders,
        immediateTenders: updatedData.immediateTenders ?? cart.immediateTenders,
        rejectedPetsitters: cart.rejectedPetsitters ?? [],
      }

      const response = (await updateCartAsync(updatedCart)) as CartData

      dispatch({ type: CartTypes.UpdateCart, payload: response })
    } catch (error) {
      console.error('Failed to update cart:', error)
    }
  }
